/**
 * Loose (pre-insert) duplicate detection for bank-statement imports — the CSV
 * wizard's Review step and the Enable Banking sync.
 *
 * WHY THIS EXISTS. The CSV dedup key embeds the file's row index, so the same
 * transaction re-exported in an overlapping date range never collides with the
 * copy already in the feed: 127 of one production org's 140 skipped lines are
 * duplicate copies from 13 overlapping exports. CAMT does not need this on its
 * OWN — it dedups exactly on the bank's own `EntryReference` — and running the
 * loose rule over a CAMT upload would only flag genuine same-day pairs.
 *
 * WHY THE SYNC NEEDS IT TOO. An Enable Banking line and the CAMT/CSV copy of
 * the same transaction live in different id spaces: the PSD2 `entry_reference`
 * (`eb:0103052906…`) and the CAMT `EntryReference` (`prtry:…|e2e:…|ref:…`) are
 * not the same string, EB rows carry no `end_to_end_id` and usually a NULL
 * `reference`, so NO exact key can ever bridge the two sources. One production
 * account mixed a 556-row statement history with a live feed and the sync
 * flagged none of its 149 copies. The sync therefore runs this rule over every
 * chunk against the feed's `source_type <> 'enablebanking'` rows before it
 * inserts — a twin lands already `skipped` / `skip_reason='duplicate'` under
 * its `eb:` key, one Undo away. (EB-vs-EB is deliberately NOT paired here: two
 * EB rows with different `eb:` ids are different lines by the bank's own word,
 * and a re-fetched EB row has the same `eb:` id and is caught exactly.)
 *
 * WHAT IT IS NOT. This never drops a row. In the wizard it flags, the Review
 * step unchecks the flagged rows, and the user decides; on the sync it flags,
 * the row is inserted pre-skipped, and the user can Undo. Exact DB dedup (the
 * partial unique index + `ON CONFLICT DO NOTHING` in
 * `bulk_insert_accounting_imports`) still runs underneath, so a wrong answer
 * here costs a checkbox or an Undo, never a line.
 *
 * TEXT IS NEVER A REQUIREMENT OR A VETO. Measured on the production account,
 * structured/text evidence confirmed ~40 % of the cross-source pairs and
 * contradicted none — so the e2e-token and remittance rules below can only
 * RANK a candidate above another, never un-flag one. Requiring text agreement
 * would leave 60 % of the duplicates unflagged.
 *
 * See docs/BANK_IMPORT.md → "Duplicate Detection".
 */
import { looseDuplicateEvidenceEnum, } from "../schemas/clearing.js";
/**
 * Prefix `buildEntryReference` stamps on a CSV row whose export carried a bank
 * transaction id (Rabobank `Volgnr`, …). The stored value is EXACTLY
 * `csvid:<id>` — no `prtry:`/`ref:` composition — so an id written by one
 * export matches the same id written by the next regardless of how the other
 * columns were mapped.
 */
export const CSV_BANK_ID_PREFIX = "csvid:";
/**
 * Strict descending by design. Both-present-equal e2e (`end_to_end`, 4) is
 * identity; a one-sided token hit (`end_to_end_token`, 3) is a substring
 * heuristic and ranks below it. The rank picks WHICH candidate a probe is
 * credited against when several share its key — it never decides WHETHER the
 * probe is flagged (the vetoes do that, and they return null). Accepted with
 * eyes open: a short spurious token (3) can out-rank an IBAN-equal candidate
 * (2); that is attribution only, never a count, and there is deliberately no
 * minimum token length in TS (the repair SQL uses ≥ 6, also confirm-only).
 */
const EVIDENCE_RANK = {
    bank_id: 6,
    reference: 5,
    end_to_end: 4,
    end_to_end_token: 3,
    counterparty_account: 2,
    remittance_text: 1,
    key_only: 0,
};
/**
 * Compare two evidence values by {@link EVIDENCE_RANK} without exporting the
 * table: positive when `a` is the stronger. The suggestion module
 * (`looseDuplicateSuggestions.ts`) picks a probe's best candidate with it, so
 * the grid and the sync rank candidates by ONE table. `EVIDENCE_RANK` itself
 * stays private — nothing outside this file should be able to invent a
 * threshold; {@link looseDuplicateStrength} is the one that exists.
 */
export function compareLooseDuplicateEvidence(a, b) {
    return EVIDENCE_RANK[a] - EVIDENCE_RANK[b];
}
/**
 * Weakest evidence the sync may act on alone. Everything below lands pending
 * with a grid chip.
 *
 * WHY HERE. `remittance_text` and `key_only` are exactly the two values that
 * can be true of two GENUINE same-day payments (a second transfer of the same
 * amount to a payee whose text is similar or absent); an IBAN match or better
 * says the bank saw the same counterparty, which two coincidental payments
 * rarely share on one day. A `counterparty_account` flag can still be wrong
 * (two direct debits from one creditor without an e2e on the EB side) — that
 * stays one Undo away and the FETCH_BALANCES `balance_delta` detector notices
 * it; the docs carry it as a known risk.
 */
export const LOOSE_DUPLICATE_AUTO_SKIP_MIN_EVIDENCE = "counterparty_account";
/**
 * `strong` iff the evidence ranks at or above
 * {@link LOOSE_DUPLICATE_AUTO_SKIP_MIN_EVIDENCE}. ONE threshold, both callers
 * (the sync's auto-skip / suggest split and the grid's `strength` on the wire).
 */
export function looseDuplicateStrength(evidence) {
    return EVIDENCE_RANK[evidence] >= EVIDENCE_RANK[LOOSE_DUPLICATE_AUTO_SKIP_MIN_EVIDENCE]
        ? "strong"
        : "weak";
}
/**
 * Every evidence value, exported so the API's per-sync tally
 * (`Record<LooseDuplicateEvidence, number>`) can be seeded from it and cannot
 * silently miss a value added here. Derived from the zod enum's `.options`
 * (the one runtime list) — COPIED, not aliased, so the exported tuple is
 * `readonly` and nobody can push into zod's own array through it. `satisfies`
 * makes a value added to the enum but missing from `EVIDENCE_RANK` a compile
 * error via the Record, and vice versa.
 */
export const LOOSE_DUPLICATE_EVIDENCE_VALUES = [
    ...looseDuplicateEvidenceEnum.options,
];
/** Trimmed non-empty string, or undefined. */
function present(value) {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
}
/** The bank transaction id an existing feed row was stored with, if any. */
function existingBankId(row) {
    const ref = present(row.entry_reference);
    if (!ref || !ref.startsWith(CSV_BANK_ID_PREFIX))
        return undefined;
    return present(ref.slice(CSV_BANK_ID_PREFIX.length));
}
/**
 * IBAN shape: two-letter country, two check digits, 11–30 alphanumerics
 * (ISO 13616 bounds the BBAN at 30). Applied AFTER normalization.
 *
 * THIS IS THE ONE HOME OF THE SHAPE GATE — not the wizard, not the API, not
 * three places. `related_party_account` is not guaranteed to be an IBAN on
 * either side: the EB mapper falls back to `other.identification` when the
 * bank sends no IBAN, and the CAMT parser has the mirror hazard (`Othr/Id`, a
 * plain account number). A BBAN on one side against the IBAN of the SAME
 * counterparty on the other would otherwise veto exactly the pairs this
 * feature exists to flag. A value that does not match is treated as ABSENT,
 * so rule 3 falls through instead of vetoing. The repair SQL's `acct_norm`
 * yields NULL on the same shape test so the two agree.
 */
const IBAN_SHAPE = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
/**
 * The counterparty IBAN a side carries, normalized (whitespace stripped,
 * upper-cased — `NL91 ABNA 0417 1643 00` and `nl91abna0417164300` are the same
 * account), or undefined when absent or not IBAN-shaped.
 */
function counterpartyIban(value) {
    const raw = present(value);
    if (!raw)
        return undefined;
    const normalized = raw.replace(/\s+/g, "").toUpperCase();
    return IBAN_SHAPE.test(normalized) ? normalized : undefined;
}
/**
 * CAMT's placeholder for "the bank filled the mandatory `EndToEndId` element
 * with nothing" (ISO 20022 requires the element, so banks write this literal).
 * Not an id, so not evidence — treated as absent on BOTH sides.
 */
const E2E_NOT_PROVIDED = "NOTPROVIDED";
/** The SEPA end-to-end id a side carries — trimmed, non-empty, not the placeholder. */
function endToEndId(value) {
    const raw = present(value);
    if (!raw || raw.toUpperCase() === E2E_NOT_PROVIDED)
        return undefined;
    return raw;
}
/**
 * THE ONE TEXT ACCESSOR both the rule-4 token scan and the rule-5 containment
 * read: `remittance_info ?? description`, per side.
 *
 * Pinned to this, not `description || remittance_info` concatenated, because
 * the two rules must scan the same string and the repair SQL's
 * `text_norm`/`remit_norm` mirror it. The CAMT side's `description` is NOT
 * folded in alongside its remittance: `buildDescription` prefixes it with
 * `Ref: <NtryRef>` / `End-to-End:` parts, so it can never be contained in the
 * EB text and only adds false substrings to scan. It is the fallback only when
 * the side stored no `remittance_info` at all.
 */
function looseText(row) {
    return row.remittance_info ?? row.description ?? undefined;
}
/**
 * Text normalization for the containment rule: lowercase, `|` → space (the
 * Rabobank blob's segment separator), collapse whitespace, trim.
 */
function normalizeText(value) {
    return value.toLowerCase().replace(/\|/g, " ").replace(/\s+/g, " ").trim();
}
/**
 * Below this, "contains" is noise: a 3-character remittance is inside almost
 * anything. Applied to the SHORTER side of the pair.
 */
const MIN_CONTAINMENT_LENGTH = 4;
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
/**
 * Does `token` appear in `text` as a whole token — bounded by start/end or a
 * non-letter, non-digit on each side, case-insensitive? Boundaries are
 * enforced so `501134589475` is NOT found inside `1501134589475`. The e2e
 * appears verbatim in the Rabobank EB text (`… | Kenmerk: 501134589475 | …`),
 * which is what this exists for.
 *
 * The boundary class is Unicode (`\p{L}` / `\p{N}` under the `u` flag), not
 * ASCII `[A-Za-z0-9]`: an ASCII class treats `é` as a separator, so the e2e
 * would be "found" glued to a Dutch word (`Kenmerkcodé501134589475`) — and
 * the text on both sides is free-form bank remittance, which is exactly where
 * accented letters live. `\p{N}` covers `\d`, so the "not inside a longer
 * number" rule is unchanged.
 */
function containsToken(text, token) {
    const pattern = new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(token)}([^\\p{L}\\p{N}]|$)`, "iu");
    return pattern.test(text);
}
/**
 * Does a probe row look like a copy of an existing feed row, GIVEN that the
 * two already share a day, an amount and a direction (the caller's key)? And
 * if so, on what evidence?
 *
 * Like-with-like, strongest evidence first:
 *
 *  1. BOTH sides carry a bank transaction id → the ids must be equal. This is
 *     the exact answer; two same-day same-amount lines with different ids are
 *     genuinely different transactions. → `bank_id`, or vetoed.
 *  2. Otherwise BOTH sides carry a reference → the references must be equal
 *     (trimmed, exact). Same reasoning one notch weaker. → `reference`, or
 *     vetoed.
 *  3. BOTH sides carry an IBAN-shaped counterparty account (normalized — see
 *     `counterpartyIban`) → they must be equal, or the pair is vetoed: same
 *     day, same amount, two different counterparties is two transactions.
 *     Equal does NOT decide it yet — the match is remembered and the rules
 *     KEEP GOING, because rule 4 can still veto (same creditor, two direct
 *     debits on one day, different end-to-end ids).
 *  4. End-to-end id (present = trimmed, non-empty, not `NOTPROVIDED`):
 *     - BOTH present → equal ? `end_to_end` : vetoed.
 *     - ONE side only → if it appears as a whole token in the OTHER side's
 *       text (`looseText`) → `end_to_end_token`. Not found → fall through;
 *       a missing token is not evidence of anything (the EB text does not
 *       always carry the `Kenmerk:` segment).
 *     - Neither → fall through.
 *     Symmetric on purpose: it is source-agnostic and free, and while EB rows
 *     never carry an e2e today (so on the sync only "existing CAMT e2e found
 *     inside the EB text" can fire), the wizard direction and a future
 *     CAMT-after-sync check read it the other way round.
 *  5. Fall-through → `counterparty_account` if rule 3 saw equal IBANs; else
 *     `remittance_text` if either side's normalized text contains the other's
 *     (shorter side ≥ 4 chars); else `key_only`. Day + amount + direction is
 *     all the evidence there is, and it is what the production duplicates
 *     look like — the pair IS flagged, text only says how confidently.
 *
 * Descriptions are NEVER a veto: the same transaction's text differs between
 * two exports of the same account (and between a statement and the PSD2
 * feed), so refusing a pair over it would un-flag exactly the rows this check
 * exists for. Rules 4 (token) and 5 only ever CONFIRM.
 *
 * "Like-with-like" is load-bearing in the fall-through: if only ONE side
 * carries an id (or a reference, or an IBAN), that is a mapping/vintage/source
 * difference between the two lines, not evidence that they differ — so it
 * falls through to the next rule rather than refusing the pair.
 *
 * @returns the evidence the pair was decided on, or `null` when a rule vetoed
 *          it (NOT a duplicate). Never `undefined`.
 */
export function looseDuplicateEvidence(probe, existing) {
    // Rule 1 — bank transaction id on both sides.
    const probeId = present(probe.bank_transaction_id);
    const existingId = existingBankId(existing);
    if (probeId && existingId)
        return probeId === existingId ? "bank_id" : null;
    // Rule 2 — reference on both sides.
    const probeRef = present(probe.reference);
    const existingRef = present(existing.reference);
    if (probeRef && existingRef)
        return probeRef === existingRef ? "reference" : null;
    // Rule 3 — counterparty IBAN on both sides: a veto when they differ, a
    // remembered confirmation when they agree. Never decisive on its own.
    const probeIban = counterpartyIban(probe.related_party_account);
    const existingIban = counterpartyIban(existing.related_party_account);
    let sameCounterparty = false;
    if (probeIban && existingIban) {
        if (probeIban !== existingIban)
            return null;
        sameCounterparty = true;
    }
    // Rule 4 — end-to-end id.
    const probeE2e = endToEndId(probe.end_to_end_id);
    const existingE2e = endToEndId(existing.end_to_end_id);
    if (probeE2e && existingE2e)
        return probeE2e === existingE2e ? "end_to_end" : null;
    const oneSidedE2e = probeE2e ?? existingE2e;
    if (oneSidedE2e) {
        // Exactly one side carries it: look for it as a token in the OTHER side's text.
        const otherText = probeE2e ? looseText(existing) : looseText(probe);
        if (otherText && containsToken(otherText, oneSidedE2e))
            return "end_to_end_token";
    }
    // Rule 5 — fall-through. Flagged regardless; the value is the confidence.
    if (sameCounterparty)
        return "counterparty_account";
    const probeText = looseText(probe);
    const existingText = looseText(existing);
    if (probeText && existingText) {
        const a = normalizeText(probeText);
        const b = normalizeText(existingText);
        const shorter = a.length <= b.length ? a : b;
        const longer = shorter === a ? b : a;
        if (shorter.length >= MIN_CONTAINMENT_LENGTH && longer.includes(shorter)) {
            return "remittance_text";
        }
    }
    return "key_only";
}
/**
 * Boolean view of {@link looseDuplicateEvidence}: flagged on ANY evidence,
 * `key_only` included; false only on a veto. Unchanged signature for callers
 * that do not care which rule decided it.
 */
export function isLooseDuplicate(probe, existing) {
    return looseDuplicateEvidence(probe, existing) !== null;
}
/**
 * Grouping key both sides are bucketed by: calendar day, amount to the cent,
 * direction. `amount` is money, so two decimals is the whole precision;
 * `credit_debit_indicator` is post-flip on both sides.
 *
 * Exported so the stored-row pairing (`looseDuplicateSuggestions.ts`) buckets
 * by the SAME key — a pair the grid suggests must be a pair the sync would
 * have seen, and vice versa.
 */
export function looseDuplicateGroupKey(bookingDate, amount, indicator) {
    return `${bookingDate}|${Number(amount).toFixed(2)}|${indicator}`;
}
const groupKey = looseDuplicateGroupKey;
function groupBy(rows, key) {
    const groups = new Map();
    for (const row of rows) {
        const k = key(row);
        const bucket = groups.get(k);
        if (bucket)
            bucket.push(row);
        else
            groups.set(k, [row]);
    }
    return groups;
}
/**
 * Pair a parsed file's rows against the feed rows already stored for the same
 * bank account, and return the flagged ones with the evidence each was
 * decided on.
 *
 * COUNT-AWARE. A file's *m* rows with key K against the feed's *n* rows with
 * key K flag at most `min(n, m)` — three copies in the file against one in the
 * feed flag one, not three, so re-importing a file that legitimately contains
 * repeated lines does not bury the user in false positives. On the sync path,
 * where a run is inserted in 500-row chunks, the caller carries the
 * consumption across chunks itself (it filters the existing rows it has
 * already paired out of each chunk's `existingRows`), so two EB rows on one
 * key in different chunks cannot both pair with one CAMT row.
 *
 * ORDER IS THE CONTRACT (both sides):
 *  - `fileRows` must arrive in file order (ascending `line_index`). Grouping
 *    preserves array order, so within a key the file's FIRST copy is the one
 *    flagged. The wire schema carries no `line_index` — the caller's array
 *    order IS that order.
 *  - `existingRows` must arrive in server order (`created_at, id`), which is
 *    what the endpoint's `.order()` chain produces. Chunking the read by
 *    amount cannot disturb this: a key's rows all share one amount, so they
 *    all come back inside a single chunk.
 *
 * CANDIDATE CHOICE. Among a probe's unconsumed same-key candidates the one
 * with the highest `EVIDENCE_RANK` wins; ties go to the first in server order
 * — which is exactly today's behaviour when every candidate is `key_only`, so
 * the pre-cross-source pairings are unchanged.
 *
 * Matching is GREEDY per probe (best eligible unconsumed row wins for THIS
 * probe, in file order), not maximum bipartite matching. A pathological set —
 * probe A matching only existing 2, probe B matching either — can leave B
 * paired with 2 and A unflagged. The cost of that is one extra checkbox in a
 * warn-only step, or one pre-skip the user has to click themselves on the
 * sync; the cost of a matching algorithm is a maintenance burden nobody can
 * review. (The repair SQL orders pairs GLOBALLY by evidence instead, so its
 * attribution can differ from the sync's on a shared key — same flag set,
 * possibly a different partner credited.)
 *
 * Existing rows of EVERY status count toward *n*, `skipped`-as-duplicate
 * included: a third re-export must be able to flag against the copies the
 * second export already left behind.
 *
 * @returns flagged probe `client_id` → the existing row it duplicates plus the
 *          evidence. Rows that are not duplicates are absent (never present
 *          with a null).
 */
export function pairLooseDuplicatesWithEvidence(fileRows, existingRows) {
    const flagged = new Map();
    if (fileRows.length === 0 || existingRows.length === 0)
        return flagged;
    const existingByKey = groupBy(existingRows, (row) => groupKey(row.booking_date, row.amount, row.credit_debit_indicator));
    // Consumption is what bounds the flags at min(n, m): an existing row pairs
    // with at most one file row, across the whole call.
    const consumed = new Set();
    for (const probe of fileRows) {
        const candidates = existingByKey.get(groupKey(probe.booking_date, probe.amount, probe.credit_debit_indicator));
        if (!candidates)
            continue;
        // Best evidence wins; a strict `>` keeps the FIRST candidate in server
        // order on a tie, so a key whose candidates are all `key_only` pairs
        // exactly as it did before evidence existed.
        let best;
        for (const row of candidates) {
            if (consumed.has(row.id))
                continue;
            const evidence = looseDuplicateEvidence(probe, row);
            if (evidence === null)
                continue;
            if (!best || EVIDENCE_RANK[evidence] > EVIDENCE_RANK[best.evidence]) {
                best = { existing: row, evidence };
            }
        }
        if (!best)
            continue;
        consumed.add(best.existing.id);
        flagged.set(probe.client_id, best);
    }
    return flagged;
}
/**
 * {@link pairLooseDuplicatesWithEvidence} without the evidence — the bare
 * `client_id` → existing row map the CSV wizard's endpoint has always
 * returned. Unchanged signature; same pairings.
 */
export function pairLooseDuplicates(fileRows, existingRows) {
    const flagged = new Map();
    for (const [clientId, { existing }] of pairLooseDuplicatesWithEvidence(fileRows, existingRows)) {
        flagged.set(clientId, existing);
    }
    return flagged;
}
//# sourceMappingURL=looseDuplicate.js.map